/**
 * The mountain loader (AB#6639, "C · Road to the plant"): a range over a
 * valley with a small dairy plant, and a road that a vehicle drives along to
 * it. The sun crosses in an arc, lighting the ridge from above; after sunset a
 * night with stars, a crescent moon, lit windows and headlights, then dawn
 * again. Framework-free so the boot screen (index.html, before React) and the
 * kit's `MountainLoader` share it.
 *
 * Indeterminate: the day cycle loops. Determinate: progress 0–1 is the time of
 * day, dawn to evening, and the vehicle arrives at 1. Reduced motion: a still
 * early-evening frame (windows just on).
 */

export type MountainLoaderOptions = {
  /** 'auto' follows prefers-color-scheme. */
  theme?: 'light' | 'dark' | 'auto'
  /** One cycle in ms. */
  cycleMs?: number
  /** null = indeterminate (day cycle); 0–1 = progress as the time of day. */
  progress?: number | null
  /**
   * The sky's tint (default true). Off on a card or panel surface: the tint is
   * drawn for the page background and would read as a box on another colour;
   * without it the ranges, sun, moon and stars sit straight on the surface.
   */
  sky?: boolean
}

export type MountainLoader = {
  setProgress: (progress: number | null) => void
  destroy: () => void
}

const NS = 'http://www.w3.org/2000/svg'
const RIDGE = 'M-10 95 L30 60 L60 80 L100 48 L130 74 L170 52 L215 95'
const VALLEY = 'M-5 120 L-5 84 L50 80 L100 84 L150 78 L205 82 L205 120 Z'
const ROAD = 'M30 122 C60 112 40 102 80 98 C110 95 110 90 130 83'
const HALL = 'M117 82V72l4-4v4l4-4v4l4-4v4l4-4v14Z'
const WINDOWS = [119, 123, 127]
const DAY = 0.65
const DASH = 46

type Rgb = [number, number, number]
const hex = (h: string): Rgb => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb
const mix = (a: string, b: string, t: number): string => {
  const [x, y] = [hex(a), hex(b)]
  return `rgb(${x.map((v, i) => Math.round(v + (y[i] - v) * t)).join(',')})`
}
const smooth = (t: number): number => {
  const c = Math.min(1, Math.max(0, t))
  return c * c * (3 - 2 * c)
}

// The sky is only a faint tint over the page background (`sky` is its opacity at its strongest).
const PALETTE = {
  light: {
    warm: '#f0b98a',
    high: '#c9d6f0',
    night: '#1c2242',
    sky: 0.45,
    back: [0.3, 0.45],
    frontLow: '#030416',
    frontHigh: '#2a2c45',
    road: '#8a90aa',
  },
  dark: {
    warm: '#8a3f3a',
    high: '#2b3466',
    night: '#0d1230',
    sky: 0.55,
    back: [0.45, 0.65],
    frontLow: '#1a1d38',
    frontHigh: '#30355a',
    road: '#8590c4',
  },
}

const el = <K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
  parent: Element,
): SVGElementTagNameMap[K] => {
  const node = document.createElementNS(NS, tag)
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, String(v)))
  parent.appendChild(node)
  return node
}

export const mountMountainLoader = (
  container: Element,
  {theme = 'auto', cycleMs = 4500, progress = null, sky: tinted = true}: MountainLoaderOptions = {},
): MountainLoader => {
  const svg = el(
    'svg',
    {viewBox: '0 -16 200 136', width: '100%', 'aria-hidden': 'true', focusable: 'false'},
    container,
  )
  // No frame: every edge fades into the page, so the scene grows out of it.
  const id = `ml-${Math.random().toString(36).slice(2)}`
  const defs = el('defs', {}, svg)
  const fadeX = el('linearGradient', {id: `${id}-x`}, defs)
  ;[
    [0, 0],
    [0.1, 1],
    [0.9, 1],
    [1, 0],
  ].forEach(([o, a]) => el('stop', {offset: o, 'stop-color': '#fff', 'stop-opacity': a}, fadeX))
  const fadeY = el('linearGradient', {id: `${id}-y`, x2: 0, y2: 1}, defs)
  ;[
    [0, 0],
    [0.12, 1],
    [0.75, 1],
    [1, 0],
  ].forEach(([o, a]) => el('stop', {offset: o, 'stop-color': '#fff', 'stop-opacity': a}, fadeY))
  el(
    'rect',
    {y: -16, width: 200, height: 136, fill: `url(#${id}-x)`},
    el('mask', {id: `${id}-mx`}, defs),
  )
  el(
    'rect',
    {y: -16, width: 200, height: 136, fill: `url(#${id}-y)`},
    el('mask', {id: `${id}-my`}, defs),
  )
  const scene = el('g', {mask: `url(#${id}-mx)`}, el('g', {mask: `url(#${id}-my)`}, svg))
  const sky = el('rect', {y: -16, width: 200, height: 136}, scene)
  const stars = Array.from({length: 12}, (_, i) =>
    el(
      'circle',
      {cx: ((i * 53) % 190) + 5, cy: ((i * 37) % 50) + 6, r: i % 3 ? 0.7 : 1.1, fill: '#fff'},
      scene,
    ),
  )
  const halo = el('circle', {r: 15, fill: '#cb793a', opacity: 0.25}, scene)
  const sun = el('circle', {r: 7, fill: '#cb793a'}, scene)
  const moon = el('path', {d: 'M0 -7A7 7 0 1 0 0 7A5 7 0 1 1 0 -7Z', fill: '#e8ecf7'}, scene)
  const back = el('path', {d: `${RIDGE} Z`, fill: '#923e4b'}, scene)
  // The rim light, on the ridge: a wide soft glow under a thin bright core. It fades out above
  // the plant (x 95–153), so it never outlines the chimney and hall.
  const fadePlant = el(
    'linearGradient',
    {id: `${id}-p`, gradientUnits: 'userSpaceOnUse', x2: 200, y2: 0},
    defs,
  )
  ;[
    [0, 1],
    [0.47, 1],
    [0.53, 0],
    [0.73, 0],
    [0.79, 1],
    [1, 1],
  ].forEach(([o, a]) => el('stop', {offset: o, 'stop-color': '#fff', 'stop-opacity': a}, fadePlant))
  el(
    'rect',
    {x: -20, y: -16, width: 240, height: 136, fill: `url(#${id}-p)`},
    el('mask', {id: `${id}-mp`}, defs),
  )
  const rimLayer = el('g', {mask: `url(#${id}-mp)`}, scene)
  const rims = [4, 1.2].map((width) =>
    el(
      'path',
      {
        d: RIDGE,
        fill: 'none',
        'stroke-width': width,
        'stroke-linecap': 'round',
        'stroke-dasharray': `${DASH} 2000`,
      },
      rimLayer,
    ),
  )
  const valley = el('path', {d: VALLEY}, scene)
  // The plant, just right of centre on the valley floor (where the road ends, clear of the sunset): silos, chimney, sawtooth hall, tank.
  const plant = el('g', {}, scene)
  el('rect', {x: 105, y: 66, width: 5, height: 16, rx: 2.5}, plant)
  el('rect', {x: 111, y: 68, width: 5, height: 14, rx: 2.5}, plant)
  el('rect', {x: 130, y: 60, width: 2, height: 12}, plant)
  el('path', {d: HALL}, plant)
  el('rect', {x: 135, y: 76, width: 6, height: 6, rx: 3}, plant)
  const windows = WINDOWS.map((x) =>
    el('rect', {x, y: 75, width: 2, height: 2, fill: '#f2c94c'}, scene),
  )
  const puffs = [0, 1, 2].map(() => el('circle', {r: 1.5}, scene))
  // The road: a soft wide band under a thin lighter track.
  const roads = [3.5, 1].map((width) =>
    el('path', {d: ROAD, fill: 'none', 'stroke-width': width, 'stroke-linecap': 'round'}, scene),
  )
  const road = roads[1]
  const glow = el('circle', {r: 4, fill: '#f2c94c'}, scene)
  const vehicle = el('circle', {r: 1.1}, scene)
  const roadLength = road.getTotalLength()
  // Length along the ridge at a given x, sampled once, so the rim light sits under the light source.
  const total = rims[1].getTotalLength()
  const samples = Array.from({length: 101}, (_, i) => {
    const at = (total * i) / 100
    return [rims[1].getPointAtLength(at).x, at] as const
  })
  const lengthAt = (x: number): number => (samples.find(([sx]) => sx >= x) ?? samples[100])[1]

  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  let current = progress
  let frame = 0
  const start = performance.now()

  const draw = (now: number): void => {
    // Steam keeps rising even in determinate mode; only reduced motion stops it.
    if (!svg.isConnected) return
    const colors = PALETTE[theme === 'auto' ? (media?.matches ? 'dark' : 'light') : theme]
    // Position in the cycle: day (sun) for DAY of it, then night (moon).
    const t = still
      ? DAY * 0.96
      : current !== null
        ? current * DAY
        : ((now - start) % cycleMs) / cycleMs
    const isDay = t < DAY
    const p = isDay ? t / DAY : (t - DAY) / (1 - DAY)
    const x = 20 + 160 * p
    // Peak at y 24: the halo (r 15) keeps 8+ units of clear sky below the top fade (y ≤ 0.3).
    const y = 104 - 80 * Math.sin(Math.PI * p)
    const light = isDay ? smooth(Math.sin(Math.PI * p) * 1.4) : 0
    const night = isDay ? 0 : smooth(Math.sin(Math.PI * p) * 1.6)
    const daySky = mix(colors.warm, colors.high, light)
    sky.setAttribute('fill', isDay ? daySky : mix(colors.warm, colors.night, night))
    // A faint tint, strongest around a low sun and in deep night, never a box.
    sky.setAttribute(
      'opacity',
      String(tinted ? colors.sky * (isDay ? 0.55 + 0.45 * (1 - light) : 0.5 + 0.5 * night) : 0),
    )
    stars.forEach((star, i) =>
      star.setAttribute(
        'opacity',
        String(night * (0.55 + 0.45 * Math.sin(now / (400 + i * 70) + i))),
      ),
    )
    for (const node of [sun, halo]) {
      node.setAttribute('cx', String(x))
      node.setAttribute('cy', String(y))
      node.setAttribute('display', isDay ? '' : 'none')
    }
    moon.setAttribute('transform', `translate(${x} ${y}) rotate(-25)`)
    moon.setAttribute('display', isDay ? 'none' : '')
    back.setAttribute('opacity', String(colors.back[0] + (colors.back[1] - colors.back[0]) * light))
    const ground = mix(colors.frontLow, colors.frontHigh, light)
    valley.setAttribute('fill', ground)
    // The plant a touch lighter than the valley so it reads on both themes; the road a soft pale track.
    plant.setAttribute(
      'fill',
      mix(colors.frontHigh, colors.high, colors === PALETTE.dark ? 0.45 : 0.1),
    )
    // A faint top-edge light from the sun or moon.
    plant.setAttribute('stroke', isDay ? '#f0a774' : '#b9c6e8')
    plant.setAttribute('stroke-width', '0.4')
    plant.setAttribute('stroke-opacity', String(0.5 * (isDay ? light : night)))
    roads.forEach((r, i) => {
      r.setAttribute('stroke', colors.road)
      r.setAttribute('opacity', String(i ? 0.75 : 0.22))
    })
    // Windows light up at dusk and stay on until dawn.
    const lit = isDay ? smooth((p - 0.82) / 0.15) : 1 - smooth((p - 0.9) / 0.1)
    windows.forEach((w) => w.setAttribute('opacity', String(lit)))
    // Steam rises from the chimney, always.
    puffs.forEach((puff, i) => {
      const k = still ? i / 3 : (now / 2600 + i / 3) % 1
      puff.setAttribute('cx', String(131 + 6 * k))
      puff.setAttribute('cy', String(58 - 14 * k))
      puff.setAttribute('r', String(1.2 + 2 * k))
      puff.setAttribute('fill', mix(colors.high, '#ffffff', 0.5))
      puff.setAttribute('opacity', String(0.5 * (1 - k)))
    })
    // The vehicle drives to the plant once per cycle (or with the progress): a dim dot by day,
    // headlights at night.
    const along = current !== null ? current : still ? 0.7 : t
    const point = road.getPointAtLength(roadLength * along)
    for (const node of [vehicle, glow]) {
      node.setAttribute('cx', String(point.x))
      node.setAttribute('cy', String(point.y))
    }
    vehicle.setAttribute('fill', lit > 0.5 ? '#f2c94c' : ground)
    glow.setAttribute('opacity', String(0.45 * lit))
    // Low sun: a soft rim, so the ridge's turns never flare into a bright V.
    const strength = isDay ? 0.15 + 0.85 * light : 0.55 * night
    const at = lengthAt(x) - DASH / 2
    rims.forEach((rim, i) => {
      rim.setAttribute('stroke-dashoffset', String(-at))
      rim.setAttribute('stroke', isDay ? '#f0a774' : '#b9c6e8')
      rim.setAttribute('opacity', String(strength * (i === 0 ? 0.25 : 1)))
    })
    if (!still) frame = requestAnimationFrame(draw)
  }
  draw(performance.now())

  return {
    setProgress: (next) => {
      current = next === null ? null : Math.min(1, Math.max(0, next))
      cancelAnimationFrame(frame)
      draw(performance.now())
    },
    destroy: () => {
      cancelAnimationFrame(frame)
      svg.remove()
    },
  }
}
